import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  AlertTriangle,
  Check,
  ChevronDown,
  ChevronUp,
  ClipboardList,
  Clock,
  Download,
  Info,
  Mail,
  MessageSquare,
  Moon,
  Package,
  Phone,
  Sun,
  ThumbsDown,
  ThumbsUp,
  User,
  X,
} from 'lucide-react';
import { portalApi, PortalApiError } from '../../lib/portalApi';
import { LoadingSpinner } from '../../components/ui/LoadingSpinner';
import { useTheme } from '../../context/ThemeContext';
import type { QuoteVersion, ClientResponse, ClientResponseType } from '../../types/admin';

// ── Types ────────────────────────────────────────────────────────────────────

interface SiblingVersion {
  id: string;
  versionNumber: number;
  quoteVersionId: string;
  portalToken: string;
  quoteDate: string;
  totalQuotedCost: number;
  clientResponse?: ClientResponse;
  expiresAt?: string;
}

interface QuoteLeadContact {
  name: string;
  email: string;
  phone?: string;
  photoBase64?: string;
}

interface PortalMaterialItem {
  itemName: string;
  quantity: number;
}

interface PortalServiceItem {
  serviceName: string;
  code?: string;
  party: 'Customer' | 'Mover';
}

interface PortalData {
  version: QuoteVersion;
  org: { name: string; logoBase64?: string; brandPrimaryColor?: string; brandTitleTextColor?: string } | null;
  siblingVersions: SiblingVersion[];
  quoteLead: QuoteLeadContact | null;
  materials?: PortalMaterialItem[];
  services?: PortalServiceItem[];
}

// ── Helpers ──────────────────────────────────────────────────────────────────

function fmtCurrency(n: number): string {
  return `$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function fmtDate(iso: string | undefined): string {
  if (!iso) return '—';
  const hasTime = iso.includes('T');
  const d = hasTime ? new Date(iso) : new Date(`${iso}T00:00`);
  const dateStr = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  if (!hasTime) return dateStr;
  const timeStr = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  return `${dateStr} at ${timeStr}`;
}

// ── Response Section ─────────────────────────────────────────────────────────

function ResponseSection({
  existingResponse,
  onSubmit,
  submitting,
  brandPrimary,
  brandText,
}: {
  existingResponse?: ClientResponse;
  onSubmit: (type: ClientResponseType, message?: string) => void;
  submitting: boolean;
  brandPrimary?: string;
  brandText?: string;
}) {
  const [showChangeForm, setShowChangeForm] = useState(false);
  const [showDeclineForm, setShowDeclineForm] = useState(false);
  const [message, setMessage] = useState('');

  if (existingResponse) {
    const labels: Record<ClientResponseType, string> = {
      accepted: 'Quote Accepted',
      changes_requested: 'Changes Requested',
      declined: 'Quote Declined',
    };
    const colors: Record<ClientResponseType, string> = {
      accepted: 'bg-emerald-50 border-emerald-200 text-emerald-700',
      changes_requested: 'bg-amber-50 border-amber-200 text-amber-700',
      declined: 'bg-rose/10 border-rose/30 text-rose',
    };
    return (
      <div className={`rounded-xl border p-4 ${colors[existingResponse.type]}`}>
        <p className="font-semibold text-[15px] mb-1">{labels[existingResponse.type]}</p>
        <p className="text-[13px] opacity-80">
          Responded on {fmtDate(existingResponse.respondedAt)}
        </p>
        {existingResponse.message && (
          <p className="text-[13px] mt-2 opacity-90">{existingResponse.message}</p>
        )}
      </div>
    );
  }

  if (showChangeForm) {
    return (
      <div className="bg-white border border-stone rounded-xl p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold text-[15px] text-midnight">Request Changes</h3>
          <button
            type="button"
            onClick={() => { setShowChangeForm(false); setMessage(''); }}
            className="w-7 h-7 flex items-center justify-center rounded-lg text-slate hover:text-midnight"
          >
            <X size={14} />
          </button>
        </div>
        <textarea
          value={message}
          onChange={e => setMessage(e.target.value)}
          placeholder="Describe the changes you'd like…"
          rows={4}
          className="w-full rounded-lg border border-stone px-3 py-2.5 text-[14px] text-midnight
                     placeholder:text-slate/50 focus:outline-none focus:ring-2 focus:ring-plum/30
                     focus:border-plum resize-none mb-3"
        />
        <button
          type="button"
          disabled={!message.trim() || submitting}
          onClick={() => onSubmit('changes_requested', message.trim())}
          className="w-full h-11 rounded-xl bg-amber-500 text-white text-[14px] font-semibold
                     hover:bg-amber-600 active:opacity-80 disabled:opacity-50 transition-colors"
        >
          {submitting ? 'Submitting…' : 'Submit Change Request'}
        </button>
      </div>
    );
  }

  if (showDeclineForm) {
    return (
      <div className="bg-white border border-stone rounded-xl p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold text-[15px] text-midnight">Decline Quote</h3>
          <button
            type="button"
            onClick={() => { setShowDeclineForm(false); setMessage(''); }}
            className="w-7 h-7 flex items-center justify-center rounded-lg text-slate hover:text-midnight"
          >
            <X size={14} />
          </button>
        </div>
        <textarea
          value={message}
          onChange={e => setMessage(e.target.value)}
          placeholder="Reason for declining (optional)…"
          rows={3}
          className="w-full rounded-lg border border-stone px-3 py-2.5 text-[14px] text-midnight
                     placeholder:text-slate/50 focus:outline-none focus:ring-2 focus:ring-plum/30
                     focus:border-plum resize-none mb-3"
        />
        <button
          type="button"
          disabled={submitting}
          onClick={() => onSubmit('declined', message.trim() || undefined)}
          className="w-full h-11 rounded-xl bg-rose text-white text-[14px] font-semibold
                     hover:bg-rose/90 active:opacity-80 disabled:opacity-50 transition-colors"
        >
          {submitting ? 'Submitting…' : 'Confirm Decline'}
        </button>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2">
      <button
        type="button"
        disabled={submitting}
        onClick={() => onSubmit('accepted')}
        className={`w-full h-12 rounded-xl text-[15px] font-semibold
                   flex items-center justify-center gap-2
                   active:opacity-80 disabled:opacity-50 transition-colors ${!brandPrimary ? 'bg-emerald-600 text-white hover:bg-emerald-700' : ''}`}
        style={brandPrimary ? { backgroundColor: brandPrimary, color: brandText ?? '#FFFFFF' } : undefined}
      >
        {submitting ? <LoadingSpinner size="sm" /> : <ThumbsUp size={18} />}
        Accept Quote
      </button>
      <button
        type="button"
        onClick={() => setShowChangeForm(true)}
        className="w-full h-12 rounded-xl border-2 border-amber-400 text-amber-700 text-[15px] font-semibold
                   flex items-center justify-center gap-2
                   hover:bg-amber-50 active:opacity-80 transition-colors"
      >
        <MessageSquare size={18} />
        Request Changes
      </button>
      <button
        type="button"
        onClick={() => setShowDeclineForm(true)}
        className="w-full h-11 rounded-xl text-slate text-[14px] font-medium
                   flex items-center justify-center gap-2
                   hover:text-rose hover:bg-rose/5 transition-colors"
      >
        <ThumbsDown size={16} />
        Decline
      </button>
    </div>
  );
}

// ── Version Selector ─────────────────────────────────────────────────────────

function isExpired(expiresAt?: string): boolean {
  if (!expiresAt) return false;
  return new Date(expiresAt) < new Date();
}

function PortalVersionSelector({
  current: currentVersion,
  siblings,
  hideExpired,
}: {
  current: QuoteVersion;
  siblings: SiblingVersion[];
  hideExpired?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const allVersions = [
    {
      versionNumber: currentVersion.versionNumber,
      quoteVersionId: currentVersion.quoteVersionId,
      portalToken: currentVersion.portalToken!,
      quoteDate: currentVersion.quoteDate,
      expiresAt: currentVersion.expiresAt,
      isCurrent: true,
    },
    ...siblings.map(s => ({ ...s, isCurrent: false })),
  ].sort((a, b) => b.versionNumber - a.versionNumber);

  // When hideExpired is set, filter out expired non-current versions
  const displayVersions = hideExpired
    ? allVersions.filter(v => v.isCurrent || !isExpired(v.expiresAt))
    : allVersions;

  if (displayVersions.length <= 1) return null;

  return (
    <div className="relative mb-4">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="flex items-center gap-2 h-9 px-3 rounded-lg border border-stone bg-white
                   text-[13px] font-medium text-midnight hover:border-plum/40 transition-colors"
      >
        Version {currentVersion.versionNumber}
        <ChevronDown size={14} className={`text-slate transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <ul className="absolute left-0 top-full mt-1 z-50 w-56 bg-white border border-stone rounded-xl shadow-lg overflow-hidden">
            {displayVersions.map(v => (
              <li key={v.portalToken}>
                {v.isCurrent ? (
                  <div className="w-full text-left px-3 py-2.5 text-[13px] font-semibold text-plum bg-plum/5 flex items-center gap-2">
                    v{v.versionNumber} — {v.quoteDate}
                    <Check size={14} className="ml-auto text-plum" />
                  </div>
                ) : (
                  <a
                    href={`/portal/${v.portalToken}`}
                    className="w-full block text-left px-3 py-2.5 text-[13px] text-midnight hover:bg-mist transition-colors border-t border-stone"
                  >
                    v{v.versionNumber} — {v.quoteDate}
                  </a>
                )}
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}

// ── Dark Mode Toggle ────────────────────────────────────────────────────────

function DarkModeToggle() {
  const { theme, setPreference } = useTheme();
  const isDark = theme === 'dark';
  return (
    <button
      type="button"
      aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
      onClick={() => setPreference(isDark ? 'light' : 'dark')}
      className="shrink-0 w-9 h-9 flex items-center justify-center rounded-lg
                 text-slate hover:text-midnight hover:bg-mist transition-colors"
    >
      {isDark ? <Sun size={18} /> : <Moon size={18} />}
    </button>
  );
}

// ── Collapsible Materials Card ───────────────────────────────────────────────

function MaterialsCard({ materials }: { materials: PortalMaterialItem[] }) {
  const [open, setOpen] = useState(false);

  if (materials.length === 0) return null;
  // Same number the client quote shows ("N items"): total quantity, not line count.
  const materialsCount = materials.reduce((sum, m) => sum + m.quantity, 0);

  return (
    <div className="bg-white border border-stone rounded-xl shadow-sm overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-4 py-3 text-left"
      >
        <div className="flex items-center gap-2.5 min-w-0">
          <Package size={16} className="text-slate shrink-0" />
          <p className="font-semibold text-[14px] text-midnight">Materials</p>
          <span className="text-[12px] text-slate bg-mist rounded-full px-2 py-0.5 font-medium shrink-0">
            {materialsCount}
          </span>
        </div>
        {open
          ? <ChevronUp size={16} className="text-slate shrink-0" />
          : <ChevronDown size={16} className="text-slate shrink-0" />}
      </button>
      {open && (
        <div className="px-4 border-t border-stone divide-y divide-stone">
          {materials.map((item, i) => (
            <div key={i} className="flex items-center justify-between gap-3 py-2.5">
              <p className="text-[13px] text-midnight min-w-0">{item.itemName}</p>
              <span className="text-[13px] font-medium text-slate shrink-0">×{item.quantity}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Collapsible Scope of Services Card ──────────────────────────────────────

function ScopeOfServicesCard({ services }: { services: PortalServiceItem[] }) {
  const [open, setOpen] = useState(false);

  if (services.length === 0) return null;

  return (
    <div className="bg-white border border-stone rounded-xl shadow-sm overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-4 py-3 text-left"
      >
        <div className="flex items-center gap-2.5 min-w-0">
          <ClipboardList size={16} className="text-slate shrink-0" />
          <p className="font-semibold text-[14px] text-midnight">Scope of Services</p>
          <span className="text-[12px] text-slate bg-mist rounded-full px-2 py-0.5 font-medium shrink-0">
            {services.length}
          </span>
        </div>
        {open
          ? <ChevronUp size={16} className="text-slate shrink-0" />
          : <ChevronDown size={16} className="text-slate shrink-0" />}
      </button>
      {open && (
        <div className="px-4 border-t border-stone divide-y divide-stone">
          {services.map((service, i) => (
            <div key={i} className="flex items-center justify-between gap-3 py-2.5">
              <p className="text-[13px] text-midnight min-w-0">
                {service.code && <span className="font-bold mr-1.5">{service.code}</span>}
                {service.serviceName}
              </p>
              <span className="text-[12px] text-slate shrink-0">{service.party}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Page ─────────────────────────────────────────────────────────────────────

export function PortalPage() {
  const { token } = useParams<{ token: string }>();
  const [data, setData] = useState<PortalData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    const apiBase = (import.meta.env.VITE_API_URL as string | undefined) ?? '';
    const url = `${apiBase}/api/portal/${token}`;

    (async () => {
      try {
        const rawRes = await fetch(url, {
          headers: { 'Content-Type': 'application/json' },
        });
        const rawBody = await rawRes.text();

        if (!rawRes.ok) {
          const parsed = (() => { try { return JSON.parse(rawBody); } catch { return null; } })();
          if (!cancelled) {
            setError(rawRes.status === 404
              ? 'This quote link is invalid or has expired.'
              : `Server returned ${rawRes.status}: ${parsed?.error ?? rawRes.statusText}`);
          }
          return;
        }

        const parsed = JSON.parse(rawBody) as { success: boolean; data: PortalData };
        if (!cancelled) setData(parsed.data);
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load quote');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  // Add noindex meta tag
  useEffect(() => {
    const meta = document.createElement('meta');
    meta.name = 'robots';
    meta.content = 'noindex';
    document.head.appendChild(meta);
    return () => { document.head.removeChild(meta); };
  }, []);

  async function handleResponse(type: ClientResponseType, message?: string) {
    if (!token) return;
    setSubmitting(true);
    try {
      const res = await portalApi.post<{ success: boolean; data: ClientResponse }>(
        `/portal/${token}/respond`,
        { type, ...(message ? { message } : {}) },
      );
      if (data) {
        setData({
          ...data,
          version: { ...data.version, clientResponse: res.data },
        });
      }
    } catch (err) {
      if (err instanceof PortalApiError && err.status === 409) {
        // Already responded — reload to get current state
        window.location.reload();
      } else {
        alert(err instanceof Error ? err.message : 'Failed to submit response');
      }
    } finally {
      setSubmitting(false);
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-mist">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-mist px-4 text-center">
        <div className="bg-white rounded-2xl border border-stone shadow-lg p-8 max-w-md w-full">
          <p className="text-[15px] font-semibold text-midnight mb-2">Quote Not Found</p>
          <p className="text-[13px] text-slate leading-snug">
            {error || 'This link may be invalid or the quote may no longer be available.'}
          </p>
        </div>
      </div>
    );
  }

  const { version, org, siblingVersions, quoteLead, materials, services } = data;

  // Check if the current version is expired
  const versionExpired = isExpired(version.expiresAt);

  // Find the newest non-expired sibling version (if any)
  const newestNonExpiredSibling = siblingVersions
    .filter(s => s.portalToken && !isExpired(s.expiresAt))
    .sort((a, b) => b.versionNumber - a.versionNumber)[0] ?? null;

  // Find the newest sibling with a higher version number (for "newer version available" banner)
  const newerVersion = siblingVersions
    .filter(s => s.versionNumber > version.versionNumber && s.portalToken && !isExpired(s.expiresAt))
    .sort((a, b) => b.versionNumber - a.versionNumber)[0] ?? null;

  const brandPrimary = org?.brandPrimaryColor;
  const brandText = org?.brandTitleTextColor;

  // ── Expired version view ──────────────────────────────────────────────────
  if (versionExpired) {
    return (
      <div className="min-h-screen bg-mist">
        {/* Header / Org Branding */}
        <div
          className={`border-b border-stone ${!brandPrimary ? 'bg-white' : ''}`}
          style={brandPrimary ? { backgroundColor: brandPrimary } : undefined}
        >
          <div className="max-w-lg mx-auto px-4 py-4 flex items-center gap-3">
            {org?.logoBase64 && (
              <img
                src={org.logoBase64}
                alt={org.name}
                className="h-10 w-10 rounded-lg object-contain shrink-0"
              />
            )}
            <div className="min-w-0 flex-1">
              <p
                className={`font-semibold text-[15px] truncate ${!brandPrimary ? 'text-midnight' : ''}`}
                style={brandPrimary ? { color: brandText ?? '#FFFFFF' } : undefined}
              >
                {org?.name ?? 'Quote'}
              </p>
              <p
                className={`text-[12px] ${!brandPrimary ? 'text-slate' : ''}`}
                style={brandPrimary ? { color: brandText ?? '#FFFFFF', opacity: 0.8 } : undefined}
              >
                Quote for {version.customerCompanyName}
              </p>
            </div>
            {!brandPrimary && <DarkModeToggle />}
          </div>
        </div>

        <div className="max-w-lg mx-auto px-4 py-6 flex flex-col gap-4">
          {/* Version Selector (hide expired siblings) */}
          <PortalVersionSelector current={version} siblings={siblingVersions} hideExpired />

          {/* Expired notice */}
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-5 text-center">
            <div className="flex justify-center mb-3">
              <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center">
                <Clock size={24} className="text-amber-600" />
              </div>
            </div>
            <p className="font-semibold text-[16px] text-amber-800 mb-2">Quote Expired</p>
            <p className="text-[13px] text-amber-700 leading-snug mb-1">
              This quote version ({version.quoteVersionId}) expired on{' '}
              {fmtDate(version.expiresAt)}.
            </p>

            {newestNonExpiredSibling ? (
              <div className="mt-4">
                <p className="text-[13px] text-amber-700 mb-3">
                  A newer version is available:
                </p>
                <a
                  href={`/portal/${newestNonExpiredSibling.portalToken}`}
                  className="inline-flex items-center justify-center gap-2 h-11 px-6 rounded-xl
                             bg-plum text-white text-[14px] font-semibold
                             hover:bg-plum/90 active:opacity-80 transition-colors"
                >
                  View Version {newestNonExpiredSibling.versionNumber}
                </a>
              </div>
            ) : (
              <div className="mt-4">
                <p className="text-[13px] text-amber-700 mb-3">
                  Please contact your quote lead for an updated quote.
                </p>
                {quoteLead && (
                  <QuoteLeadCard quoteLead={quoteLead} orgName={org?.name} />
                )}
              </div>
            )}
          </div>

          {/* Footer */}
          <p className="text-center text-[11px] text-slate/50 mt-4 pb-4">
            Powered by IOMI Pro
          </p>
        </div>
      </div>
    );
  }

  // ── Derived: whether we have materials/services to show ──────────────────
  const hasMaterials = (materials?.length ?? 0) > 0;
  const hasServices = (services?.length ?? 0) > 0;
  const hasRightColumn = hasMaterials || hasServices;

  // ── Normal (non-expired) version view ─────────────────────────────────────
  return (
    <div className="min-h-screen bg-mist">
      {/* Header / Org Branding */}
      <div
        className={`border-b border-stone ${!brandPrimary ? 'bg-white' : ''}`}
        style={brandPrimary ? { backgroundColor: brandPrimary } : undefined}
      >
        <div className={`mx-auto px-4 py-4 flex items-center gap-3 ${hasRightColumn ? 'max-w-lg lg:max-w-4xl' : 'max-w-lg'}`}>
          {org?.logoBase64 && (
            <img
              src={org.logoBase64}
              alt={org.name}
              className="h-10 w-10 rounded-lg object-contain shrink-0"
            />
          )}
          <div className="min-w-0 flex-1">
            <p
              className={`font-semibold text-[15px] truncate ${!brandPrimary ? 'text-midnight' : ''}`}
              style={brandPrimary ? { color: brandText ?? '#FFFFFF' } : undefined}
            >
              {org?.name ?? 'Quote'}
            </p>
            <p
              className={`text-[12px] ${!brandPrimary ? 'text-slate' : ''}`}
              style={brandPrimary ? { color: brandText ?? '#FFFFFF', opacity: 0.8 } : undefined}
            >
              Quote for {version.customerCompanyName}
            </p>
          </div>
          {!brandPrimary && <DarkModeToggle />}
        </div>
      </div>

      <div className={`mx-auto px-4 py-6 ${hasRightColumn ? 'max-w-lg lg:max-w-4xl' : 'max-w-lg'}`}>
        {/* Version Selector */}
        <PortalVersionSelector current={version} siblings={siblingVersions} hideExpired />

        {/* Newer version banner */}
        {newerVersion && (
          <a
            href={`/portal/${newerVersion.portalToken}`}
            className="flex items-center gap-3 bg-blue-50 border border-blue-200 rounded-xl px-4 py-3 mb-4
                       hover:bg-blue-100 transition-colors"
          >
            <Info size={18} className="text-blue-600 shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-[13px] font-medium text-blue-800">
                A newer version (v{newerVersion.versionNumber}) is available
              </p>
              <p className="text-[12px] text-blue-600">Tap to view the latest quote</p>
            </div>
          </a>
        )}

        <div className={hasRightColumn ? 'lg:grid lg:grid-cols-[1fr_320px] lg:gap-6' : 'flex flex-col gap-4'}>
          {/* ── Left column: quote information ── */}
          <div className="flex flex-col gap-4">
            {/* Total Cost (prominent) */}
            <div className="bg-white border border-stone rounded-xl shadow-sm p-5 text-center">
              <p className="text-[11px] font-bold uppercase tracking-wider text-slate mb-2">
                Total Quoted Cost
              </p>
              <p className="font-bold text-[32px] text-midnight leading-none">
                {fmtCurrency(version.totalWithTax ?? version.totalQuotedCost)}
              </p>
              <p className="text-[12px] text-slate mt-2">
                {version.quoteVersionId} · {fmtDate(version.quoteDate)}
              </p>
              <p className="text-[12px] text-slate mt-2">
                {version.bindingQuote ? 'Binding Quote' : 'Not Binding Quote'}
                {version.expiresAt && <> · Valid until {fmtDate(version.expiresAt)}</>}
              </p>
            </div>

            {/* Quote Lead Contact */}
            {quoteLead && (
              <QuoteLeadCard quoteLead={quoteLead} orgName={org?.name} />
            )}

            {/* Quote Details */}
            <div className="bg-white border border-stone rounded-xl shadow-sm overflow-hidden">
              <div className="px-4 py-3 border-b border-stone">
                <p className="font-semibold text-[14px] text-midnight">Quote Details</p>
              </div>
              <div className="px-4 divide-y divide-stone">
                <DetailRow label="Customer" value={version.customerCompanyName} />
                {version.originAddress && (
                  <DetailRow label="Origin" value={version.originAddress} />
                )}
                {version.destinationAddress && (
                  <DetailRow label="Destination" value={version.destinationAddress} />
                )}
                {version.packingDate && (
                  <DetailRow label="Packing Date" value={fmtDate(version.packingDate)} />
                )}
                {version.preferredMoveDate && (
                  <DetailRow label="Move Date" value={fmtDate(version.preferredMoveDate)} />
                )}
                {version.moveDeadline && (
                  <DetailRow label="Deadline" value={fmtDate(version.moveDeadline)} />
                )}
              </div>
            </div>

            {/* Billing Address */}
            {(version.billingName || version.billingAddressLine1 || version.billingCity) && (
              <div className="bg-white border border-stone rounded-xl shadow-sm overflow-hidden">
                <div className="px-4 py-3 border-b border-stone">
                  <p className="font-semibold text-[14px] text-midnight">Billing Address</p>
                </div>
                <div className="px-4 divide-y divide-stone">
                  {version.billingName && (
                    <DetailRow label="Name" value={version.billingName} />
                  )}
                  {(version.billingAddressLine1 || version.billingCity) && (
                    <DetailRow
                      label="Address"
                      value={[
                        version.billingAddressLine1,
                        version.billingAddressLine2,
                        [version.billingCity, version.billingState, version.billingZipCode].filter(Boolean).join(', '),
                      ].filter(Boolean).join(', ')}
                    />
                  )}
                </div>
              </div>
            )}

            {/* Line Items */}
            <div className="bg-white border border-stone rounded-xl shadow-sm overflow-hidden">
              <div className="px-4 py-3 border-b border-stone">
                <p className="font-semibold text-[14px] text-midnight">Itemized Charges</p>
              </div>
              <div className="px-4 divide-y divide-stone">
                {version.lineItems.map((item, i) => (
                  <div key={i} className="py-3">
                    <p className="text-[13px] font-medium text-midnight mb-1">{item.name}</p>
                    <div className="flex items-center justify-between text-[12px] text-slate">
                      <span>
                        {fmtCurrency(item.rate)}/hr × {item.qty % 1 === 0 ? String(item.qty) : item.qty.toFixed(1)} hrs
                      </span>
                      <span className="font-semibold text-midnight">{fmtCurrency(item.total)}</span>
                    </div>
                  </div>
                ))}
              </div>
              {/* Tax breakdown */}
              {(version.laborTaxRate != null || version.requiresManualTaxReview) && (
                <div className="px-4 py-3 border-t border-stone space-y-1.5">
                  {version.requiresManualTaxReview ? (
                    <>
                      <div className="flex gap-2 p-2.5 bg-amber-50 border border-amber-200 rounded-lg mb-2">
                        <AlertTriangle size={14} className="shrink-0 text-amber-600 mt-0.5" />
                        <p className="text-[12px] text-amber-800 leading-snug">
                          This is an interstate move. Tax must be determined manually.
                        </p>
                      </div>
                      <div className="flex items-center justify-between">
                        <p className="text-[12px] text-slate">Subtotal</p>
                        <p className="text-[12px] text-slate tabular-nums">{fmtCurrency(version.totalQuotedCost)}</p>
                      </div>
                      <div className="flex items-center justify-between">
                        <p className="text-[12px] text-slate">
                          Sales Tax{version.taxState ? ` (${version.taxState})` : ''}
                        </p>
                        <p className="text-[12px] text-amber-600 font-medium">TBD</p>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="flex items-center justify-between">
                        <p className="text-[12px] text-slate">Subtotal</p>
                        <p className="text-[12px] text-slate tabular-nums">{fmtCurrency(version.totalQuotedCost)}</p>
                      </div>
                      <div className="flex items-center justify-between">
                        <p className="text-[12px] text-slate">
                          Sales Tax ({((version.laborTaxRate ?? 0) * 100).toFixed(2)}%{version.taxState ? ` — ${version.taxState}` : ''})
                        </p>
                        <p className="text-[12px] text-slate tabular-nums">{fmtCurrency(version.taxAmount ?? 0)}</p>
                      </div>
                      {version.laborTaxRate === 0 && version.taxState && (
                        <p className="text-[11px] text-slate italic">
                          No sales tax applies in {version.taxState}
                        </p>
                      )}
                    </>
                  )}
                </div>
              )}
              {/* Total */}
              <div className="px-4 py-3 border-t border-stone flex items-center justify-between">
                <p className="font-semibold text-[14px] text-midnight">Total</p>
                <p className="font-bold text-[18px] text-midnight">
                  {version.requiresManualTaxReview
                    ? <>{fmtCurrency(version.totalQuotedCost)}<span className="text-[12px] text-slate font-normal ml-1">+ tax</span></>
                    : fmtCurrency(version.totalWithTax ?? version.totalQuotedCost)}
                </p>
              </div>
            </div>

            {/* Mobile only: actions, PDF, materials, services below charges */}
            {hasRightColumn && (
              <div className="flex flex-col gap-4 lg:hidden">
                {version.pdfUrl && (
                  <a
                    href={version.pdfUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    download
                    className="flex items-center justify-center gap-2 h-11 rounded-xl border border-stone bg-white
                               text-[14px] font-medium text-midnight hover:border-plum/40 hover:text-plum
                               transition-colors"
                  >
                    <Download size={16} />
                    Download PDF
                  </a>
                )}
                {hasMaterials && <MaterialsCard materials={materials!} />}
                {hasServices && <ScopeOfServicesCard services={services!} />}
                <div className="mt-2">
                  <ResponseSection
                    existingResponse={version.clientResponse}
                    onSubmit={handleResponse}
                    submitting={submitting}
                    brandPrimary={brandPrimary}
                    brandText={brandText}
                  />
                </div>
              </div>
            )}

            {/* No right column: actions stay inline at all breakpoints */}
            {!hasRightColumn && (
              <>
                {version.pdfUrl && (
                  <a
                    href={version.pdfUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    download
                    className="flex items-center justify-center gap-2 h-11 rounded-xl border border-stone bg-white
                               text-[14px] font-medium text-midnight hover:border-plum/40 hover:text-plum
                               transition-colors"
                  >
                    <Download size={16} />
                    Download PDF
                  </a>
                )}
                <div className="mt-2">
                  <ResponseSection
                    existingResponse={version.clientResponse}
                    onSubmit={handleResponse}
                    submitting={submitting}
                    brandPrimary={brandPrimary}
                    brandText={brandText}
                  />
                </div>
              </>
            )}
          </div>

          {/* ── Right column (lg+): actions, PDF, materials, services ── */}
          {hasRightColumn && (
            <div className="hidden lg:flex lg:flex-col lg:gap-4">
              <ResponseSection
                existingResponse={version.clientResponse}
                onSubmit={handleResponse}
                submitting={submitting}
                brandPrimary={brandPrimary}
                brandText={brandText}
              />
              {version.pdfUrl && (
                <a
                  href={version.pdfUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  download
                  className="flex items-center justify-center gap-2 h-11 rounded-xl border border-stone bg-white
                             text-[14px] font-medium text-midnight hover:border-plum/40 hover:text-plum
                             transition-colors"
                >
                  <Download size={16} />
                  Download PDF
                </a>
              )}
              {hasMaterials && <MaterialsCard materials={materials!} />}
              {hasServices && <ScopeOfServicesCard services={services!} />}
            </div>
          )}
        </div>

        {/* Footer */}
        <p className="text-center text-[11px] text-slate/50 mt-8 pb-4">
          Powered by IOMI Pro
        </p>
      </div>
    </div>
  );
}

// ── Quote Lead Card ──────────────────────────────────────────────────────────

function QuoteLeadCard({
  quoteLead,
  orgName,
}: {
  quoteLead: QuoteLeadContact;
  orgName?: string;
}) {
  return (
    <div className="bg-white border border-stone rounded-xl shadow-sm overflow-hidden">
      <div className="px-4 py-3 border-b border-stone">
        <p className="font-semibold text-[14px] text-midnight">
          {orgName ? `${orgName} Contact` : 'Your Contact'}
        </p>
      </div>
      <div className="px-4 py-3 flex items-center gap-4">
        <div className="flex-1 flex flex-col gap-2.5">
          {quoteLead.name && (
            <div className="flex items-center gap-2.5">
              <User size={15} className="text-slate shrink-0" />
              <p className="text-[13px] font-medium text-midnight">{quoteLead.name}</p>
            </div>
          )}
          {quoteLead.email && (
            <div className="flex items-center gap-2.5">
              <Mail size={15} className="text-slate shrink-0" />
              <a
                href={`mailto:${quoteLead.email}`}
                className="text-[13px] text-plum hover:underline break-all"
              >
                {quoteLead.email}
              </a>
            </div>
          )}
          {quoteLead.phone && (
            <div className="flex items-center gap-2.5">
              <Phone size={15} className="text-slate shrink-0" />
              <a
                href={`tel:${quoteLead.phone}`}
                className="text-[13px] text-plum hover:underline"
              >
                {quoteLead.phone}
              </a>
            </div>
          )}
        </div>
        {quoteLead.photoBase64 && (
          <img
            src={quoteLead.photoBase64}
            alt={quoteLead.name}
            className="shrink-0 w-14 h-14 rounded-full object-cover"
          />
        )}
      </div>
    </div>
  );
}

// ── Detail Row ───────────────────────────────────────────────────────────────

function DetailRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-start justify-between gap-4 py-2.5">
      <p className="text-[13px] text-slate shrink-0">{label}</p>
      <p className="text-[13px] font-medium text-midnight text-right">{value}</p>
    </div>
  );
}
